import type { FC } from 'react';
import { CircleDashed, CircleCheck, Filter, SlidersHorizontal, TrendingUp } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { SecondaryButton } from './procurement-parts.tsx';

/**
 * The live Procurement Brief panel.
 *
 * A sourcing conversation asks the buyer for six or seven facts in a row, and this
 * panel is the ledger of what Shivi actually heard. Two rules of the design:
 *
 * 1. **A blank row stays visibly blank.** "Not yet provided" in muted italics is
 *    the whole point — the buyer can see what is still open and what a shortlist
 *    is about to be based on, rather than trusting a summary paragraph.
 * 2. **Labels live here, values come from the case.** The server sends field keys
 *    and the buyer's own words; the wording of every label and status is this
 *    component's, so the panel translates without the state machine changing.
 */

const messages = defineMessages({
  eyebrow: {
    id: 'briefPanel.eyebrow',
    defaultMessage: 'Live document',
    description: 'Eyebrow marking the brief panel as a document that keeps updating.',
  },
  title: {
    id: 'briefPanel.title',
    defaultMessage: 'Procurement brief',
    description: 'Title of the live procurement brief panel.',
  },
  product: {
    id: 'briefPanel.field.product',
    defaultMessage: 'Product',
    description: 'Panel row label for what the buyer is sourcing.',
  },
  quantity: {
    id: 'briefPanel.field.quantity',
    defaultMessage: 'Quantity',
    description: 'Panel row label for how many units the buyer needs.',
  },
  location: {
    id: 'briefPanel.field.location',
    defaultMessage: 'Location',
    description: 'Panel row label for where the goods must be delivered.',
  },
  budget: {
    id: 'briefPanel.field.budget',
    defaultMessage: 'Budget',
    description: 'Panel row label for the buyer target price.',
  },
  timeline: {
    id: 'briefPanel.field.timeline',
    defaultMessage: 'Timeline',
    description: 'Panel row label for when delivery is needed.',
  },
  customization: {
    id: 'briefPanel.field.customization',
    defaultMessage: 'Customization requirements',
    description: 'Panel row label for material, branding and finishing asks.',
  },
  special: {
    id: 'briefPanel.field.special',
    defaultMessage: 'Special requirements',
    description: 'Panel row label for standards, samples and other conditions.',
  },
  notProvided: {
    id: 'briefPanel.notProvided',
    defaultMessage: 'Not yet provided',
    description: 'Placeholder shown in a brief row the buyer has not filled in yet.',
  },
  none: {
    id: 'briefPanel.none',
    defaultMessage: 'None specified',
    description: 'Placeholder for requirement rows where the buyer stated no requirements.',
  },
  statusGathering: {
    id: 'briefPanel.status.gathering',
    defaultMessage: 'Gathering information',
    description: 'Status while the brief is still missing something matching needs.',
  },
  statusReady: {
    id: 'briefPanel.status.ready',
    defaultMessage: 'Ready for supplier matching',
    description: 'Status once the brief holds enough to search the directory.',
  },
  statusMatched: {
    id: 'briefPanel.status.matched',
    defaultMessage: 'Suppliers matched',
    description: 'Status once a shortlist has been produced from this brief.',
  },
  stillNeeded: {
    id: 'briefPanel.stillNeeded',
    defaultMessage: 'Still needed before I can search: {fields}',
    description: 'Line listing the rows a search still requires.',
  },
  revise: {
    id: 'briefPanel.revise',
    defaultMessage: 'Correct something',
    description: 'Button that lets the buyer change a value on the brief.',
  },
  rulesHeading: {
    id: 'briefPanel.rules.heading',
    defaultMessage: 'How Shivi matches suppliers',
    description: 'Heading of the collapsible matching-rules disclosure.',
  },
  rulesSummary: {
    id: 'briefPanel.rules.summary',
    defaultMessage: '{count} rules · run in code, not guessed by the model',
    description: 'Caption under the matching-rules heading.',
  },
  ruleFilter: {
    id: 'briefPanel.rules.kind.filter',
    defaultMessage: 'Filter',
    description: 'Tag marking a matching rule that disqualifies suppliers.',
  },
  ruleBoost: {
    id: 'briefPanel.rules.kind.boost',
    defaultMessage: 'Boost',
    description: 'Tag marking a matching rule that moves a supplier up the ranking.',
  },
  ruleCategory: {
    id: 'briefPanel.rules.categoryFilter',
    defaultMessage:
      'A supplier enters the eligible pool only if our directory lists it for the category you are buying.',
    description: 'Plain-language statement of the category hard filter.',
  },
  ruleMoq: {
    id: 'briefPanel.rules.moqFilter',
    defaultMessage:
      'Your quantity must reach the supplier’s stated minimum order, otherwise it is disqualified.',
    description: 'Plain-language statement of the minimum-order hard filter.',
  },
  ruleVerified: {
    id: 'briefPanel.rules.verifiedBoost',
    defaultMessage:
      'Shivurt-verified suppliers score higher, and an unverified one never enters your shortlist.',
    description: 'Plain-language statement of the verification ranking boost.',
  },
  ruleLocation: {
    id: 'briefPanel.rules.locationBoost',
    defaultMessage:
      'A supplier whose stated coverage includes your delivery point scores above one that is merely in the same country.',
    description: 'Plain-language statement of the proximity ranking boost.',
  },
});

type PanelField =
  | 'product'
  | 'quantity'
  | 'location'
  | 'budget'
  | 'timeline'
  | 'customization'
  | 'special';

const FIELD_ORDER: PanelField[] = [
  'product',
  'quantity',
  'location',
  'budget',
  'timeline',
  'customization',
  'special',
];

const FIELD_LABELS: Record<PanelField, (typeof messages)[keyof typeof messages]> = {
  product: messages.product,
  quantity: messages.quantity,
  location: messages.location,
  budget: messages.budget,
  timeline: messages.timeline,
  customization: messages.customization,
  special: messages.special,
};

/** Requirement rows read "None specified"; a fact the buyer owes reads "Not yet provided". */
const OPTIONAL_ROWS: PanelField[] = ['customization', 'special'];

type RuleId = 'category_filter' | 'moq_filter' | 'verified_boost' | 'location_boost';

const RULES: Record<RuleId, { kind: 'filter' | 'boost'; message: (typeof messages)['ruleCategory'] }> =
  {
    category_filter: { kind: 'filter', message: messages.ruleCategory },
    moq_filter: { kind: 'filter', message: messages.ruleMoq },
    verified_boost: { kind: 'boost', message: messages.ruleVerified },
    location_boost: { kind: 'boost', message: messages.ruleLocation },
  };

interface PanelRow {
  field: PanelField;
  value?: string;
  caption?: string;
  details: string[];
}

function readRows(value: unknown): PanelRow[] {
  const rows = arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => {
      const field = str(entry.field) as PanelField;
      const rowValue = optStr(entry.value);
      const caption = optStr(entry.caption);
      return {
        field,
        ...(rowValue ? { value: rowValue } : {}),
        ...(caption ? { caption } : {}),
        details: arr(entry.details).map(str).filter(Boolean),
      };
    })
    .filter((entry) => FIELD_ORDER.includes(entry.field));
  // The panel is a fixed form, not a list: a row the model left out still has to
  // appear, blank, or the buyer cannot see what is missing.
  return FIELD_ORDER.map(
    (field) => rows.find((row) => row.field === field) ?? { field, details: [] },
  );
}

export const BriefPanel: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const rows = readRows(node.props.fields);
  const reference = optStr(node.props.reference);
  const question = optStr(node.props.question);
  const status = str(node.props.status);
  const stillNeeded = arr(node.props.stillNeeded)
    .map(str)
    .filter((entry): entry is PanelField => FIELD_ORDER.includes(entry as PanelField));
  const rules = arr(node.props.matchingRules)
    .map(str)
    .filter((entry): entry is RuleId => entry in RULES);

  const ready = status === 'ready' || status === 'matched';
  const statusMessage =
    status === 'matched'
      ? messages.statusMatched
      : status === 'ready'
        ? messages.statusReady
        : messages.statusGathering;
  const StatusIcon = ready ? CircleCheck : CircleDashed;

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-elevated">
      <header className="bg-brand-ink px-6 py-5 text-brand-ink-foreground">
        <p className="text-body-xs font-semibold uppercase tracking-caps opacity-70">
          {intl.formatMessage(messages.eyebrow)}
        </p>
        <h2 className="mt-1 font-display text-heading-md font-bold tracking-tight">
          {intl.formatMessage(messages.title)}
        </h2>
        {reference ? (
          <p className="mt-1 text-body-xs uppercase tracking-caps opacity-70" translate="no">
            {reference}
          </p>
        ) : null}
      </header>

      <dl className="flex flex-col divide-y divide-border px-6">
        {rows.map((row) => {
          const filled = Boolean(row.value) || row.details.length > 0;
          return (
            <div
              key={row.field}
              className="grid gap-1 py-3 sm:grid-cols-[minmax(0,12rem)_1fr] sm:gap-4"
            >
              <dt className="text-body-sm font-semibold text-muted-foreground">
                {intl.formatMessage(FIELD_LABELS[row.field])}
              </dt>
              <dd className="min-w-0">
                {filled ? (
                  <>
                    {row.value ? (
                      <p className="break-words text-body-base font-medium text-foreground">
                        {row.value}
                      </p>
                    ) : null}
                    {row.details.length > 0 ? (
                      <ul className="flex flex-col gap-0.5">
                        {row.details.map((line) => (
                          <li key={line} className="break-words text-body-base text-foreground">
                            {line}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {row.caption ? (
                      <p className="mt-0.5 text-body-xs text-muted-foreground-subtle">
                        {row.caption}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-body-base italic text-muted-foreground-subtle">
                    {intl.formatMessage(
                      OPTIONAL_ROWS.includes(row.field) ? messages.none : messages.notProvided,
                    )}
                  </p>
                )}
              </dd>
            </div>
          );
        })}
      </dl>

      <div className="flex flex-col gap-3 px-6 py-5">
        {question ? (
          <p className="text-body-base font-medium text-foreground">{question}</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-body-xs font-semibold ${
              ready ? 'bg-success/12 text-success' : 'bg-warning/14 text-warning'
            }`}
          >
            <StatusIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            {intl.formatMessage(statusMessage)}
          </span>
          <SecondaryButton
            label={intl.formatMessage(messages.revise)}
            onClick={() => dispatch?.('reviseBrief', {})}
          />
        </div>

        {stillNeeded.length > 0 ? (
          <p className="text-body-sm text-muted-foreground">
            {intl.formatMessage(messages.stillNeeded, {
              fields: intl.formatList(
                stillNeeded.map((field) => intl.formatMessage(FIELD_LABELS[field]).toLowerCase()),
                { type: 'conjunction' },
              ),
            })}
          </p>
        ) : null}
      </div>

      {rules.length > 0 ? (
        <details className="border-t border-border bg-muted px-6 py-4">
          <summary className="cursor-pointer list-none">
            <span className="flex items-center gap-2 text-body-sm font-semibold text-foreground">
              <SlidersHorizontal aria-hidden="true" className="h-4 w-4 text-brand-accent" />
              {intl.formatMessage(messages.rulesHeading)}
            </span>
            <span className="mt-0.5 block pl-6 text-body-xs text-muted-foreground-subtle">
              {intl.formatMessage(messages.rulesSummary, { count: rules.length })}
            </span>
          </summary>
          <ul className="mt-3 flex flex-col gap-2">
            {rules.map((id) => {
              const rule = RULES[id];
              const RuleIcon = rule.kind === 'filter' ? Filter : TrendingUp;
              return (
                <li key={id} className="flex items-start gap-2 text-body-sm text-muted-foreground">
                  <RuleIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" />
                  <span className="min-w-0">
                    <span className="mr-1.5 font-semibold uppercase tracking-caps text-body-xs text-muted-foreground-subtle">
                      {intl.formatMessage(
                        rule.kind === 'filter' ? messages.ruleFilter : messages.ruleBoost,
                      )}
                    </span>
                    {intl.formatMessage(rule.message)}
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </section>
  );
};
